<script lang="ts" setup>
import { GpSwitch, GpTooltip } from '@gportal/uikit';
import { BillingPeriod } from 'gpcore-grpc/gpcore/api/cloud/v1/billing_pb';
import { type Flavour, FlavourAvailability } from 'gpcore-grpc/gpcore/api/cloud/v1/flavour_pb';
import type { Datacenter } from 'gpcore-grpc/gpcore/api/cloud/v1/region_pb';
import { computed, onMounted, toRef, watch } from 'vue';
import AsyncSectionContent from '../components/AsyncSectionContent.vue';
import SectionLoadingMessage from '../components/SectionLoadingMessage.vue';
import SectionMessage from '../components/SectionMessage.vue';
import OrderSection from '../OrderSection.vue';
import useFlavoursectionData from './composables/useFlavoursectionData';
import FlavourPickerItem from './FlavourItem.vue';
import SectionErrorMessage from '../components/SectionErrorMessage.vue';

const flavour = defineModel<Flavour>({
  default: null,
});
const billingPeriod = defineModel<BillingPeriod>('billingPeriod', {
  default: BillingPeriod.HOURLY,
});

const props = defineProps<{
  currentDatacenter: Datacenter;
}>();

const handleBillingPeriodSelect = (period: BillingPeriod) => {
  billingPeriod.value = period;
};

const datacenter = toRef(props, 'currentDatacenter');
const {
  data: flavours,
  displayData: displayFlavours,
  isLoading,
  error,
  fetch,
  showSoldOut,
} = useFlavoursectionData(datacenter);

const preselectFlavour = () => {
  // we have to find the the first available flavour in the list
  // this also includes the case, where all flavours are out of stock, then set the model value flavour to null
  flavour.value = displayFlavours.value.find(
    (fl) => fl.availability !== FlavourAvailability.OUT_OF_STOCK,
  );
};

const refreshData = async () => {
  flavour.value = null; // reset flavour selection
  await fetch();
  preselectFlavour();
};

const handleFlavourSelect = (fl: Flavour) => {
  if (fl.availability !== FlavourAvailability.OUT_OF_STOCK) {
    flavour.value = fl;
  }
};
const isAllFlavoursRegularyAvailable = computed(() =>
  flavours.value.every(
    (fl) =>
      fl.availability !== FlavourAvailability.LOW &&
      fl.availability !== FlavourAvailability.OUT_OF_STOCK &&
      fl.availability !== FlavourAvailability.UNSPECIFIED,
  ),
);

watch(showSoldOut, refreshData);

watch(datacenter, refreshData);

onMounted(() => {
  refreshData();
});
</script>

<template>
  <OrderSection
    id="flavour"
    key="flavour"
    v-playwright="'nodes-create-flavour'"
    :is-done="flavour != null"
    :is-loading="isLoading"
    pre-title="Step 2"
    section-key="flavour"
    title="Choose a flavour"
  >
    <template #buttons>
      <GpTooltip content="Show sold out flavours for region." placement="left">
        <GpSwitch
          v-model="showSoldOut"
          v-playwright="'nodes-create-flavour-show-sold-out'"
          class="tw-mt-4"
          input-id="hide-sold-out"
          name="hideSoldOut"
        />
      </GpTooltip>
    </template>

    <AsyncSectionContent :error="error" :is-empty="flavours.length === 0" :is-loading="isLoading">
      <template #loading>
        <SectionLoadingMessage>Loading flavours...</SectionLoadingMessage>
      </template>

      <template #error>
        <SectionErrorMessage v-playwright="'nodes-create-flavour-error'" :error="error">
          Error while loading flavours
        </SectionErrorMessage>
      </template>

      <template #empty>
        <SectionMessage v-playwright="'nodes-create-flavour-empty'">
          <span class="tw-font-bold tw-uppercase">
            No flavours available in this region right now.
          </span>
          <br />
          <small>Please check back later.</small>
        </SectionMessage>
      </template>

      <div
        v-playwright="'nodes-create-flavour-grid'"
        class="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 lg:tw-grid-cols-4"
      >
        <FlavourPickerItem
          v-for="(fl, index) in displayFlavours"
          :key="fl.id"
          v-playwright="`nodes-create-flavour-item-${index}`"
          :current-billing-period="billingPeriod"
          :flavour="fl"
          :is-all-flavours-available="isAllFlavoursRegularyAvailable"
          :is-selected="flavour != null && fl.id === flavour.id"
          class="tw-h-full"
          @billing-period-selected="handleBillingPeriodSelect"
          @flavour-selected="handleFlavourSelect(fl)"
        />

        <!-- <div class="tw-col-span-full tw-w-full">
          <div class="row">
            <GpDropdown
              v-if="billingPeriodOptions.length >= 1"
              class="tw-w-full md:tw-w-1/2 lg:tw-w-1/4"
              :model-value="billingPeriod"
              name="billingPeriod"
              :data="billingPeriodOptions"
              :input-id="`flavour-${flavour.id}-billing-period`"
              @update:model-value="($event) => handleBillingPeriodChange($event as BillingPeriod)"
            >
              <template #label>
                <GpTooltip
                  content="Selecting a monthly billing period results in a monthly contract, but
                you receive a discount against using hourly pricing."
                >
                  <span class="tw-text-sm tw-font-bold">
                    Billing Period
                    <FontAwesomeIcon icon="question-circle" class="tw-ml-2" />
                  </span>
                </GpTooltip>
              </template>
            </GpDropdown>
          </div>
        </div>-->
      </div>
    </AsyncSectionContent>
  </OrderSection>
</template>
